import type { LegalDocumentSection } from './legal-document';
import {
  PRIVACY_POLICY_OWNER_ADDRESS,
  PRIVACY_POLICY_OWNER_EMAIL,
  PRIVACY_POLICY_OWNER_NAME,
} from './privacy-policy-content';

export const TERMS_VERSION = '1.0';
export const TERMS_EFFECTIVE_DATE = '2026-09-02';
export const TERMS_EFFECTIVE_DATE_LABEL = '2 de setembro de 2026';

export const termsSections: ReadonlyArray<LegalDocumentSection> = [
  {
    id: 'introducao',
    title: 'Introdução',
    blocks: [
      {
        paragraphs: ['Os presentes Termos regem:'],
        bullets: [
          'a utilização desta Aplicação; e',
          'qualquer outro Contrato ou relação jurídica vinculativa com o Proprietário.',
        ],
      },
      {
        paragraphs: [
          'As palavras iniciadas por maiúscula são definidas na secção de Definições do presente documento. O Utilizador deve ler este documento atentamente.',
          `Esta Aplicação é prestada por ${PRIVACY_POLICY_OWNER_NAME}, ${PRIVACY_POLICY_OWNER_ADDRESS}.`,
          `E-mail de contacto do Proprietário: ${PRIVACY_POLICY_OWNER_EMAIL}.`,
        ],
      },
    ],
  },
  {
    id: 'a-saber',
    title: 'O que o Utilizador deverá saber à partida',
    blocks: [
      {
        bullets: [
          'Esta Aplicação destina-se exclusivamente a Utilizadores Comerciais, isto é, a profissionais e empresas que a utilizam no âmbito da sua atividade. Não se destina a Consumidores e não é disponibilizada a Consumidores.',
          'A relação entre o Proprietário e o Utilizador é uma relação entre profissionais, pelo que não se aplicam as normas de proteção do consumidor.',
          'Quando o Utilizador trata dados pessoais dos seus próprios clientes através desta Aplicação, o Utilizador é o Responsável pelo Tratamento e o Proprietário é Subcontratante, nos termos do Acordo de Tratamento de Dados.',
        ],
      },
    ],
  },
  {
    id: 'termos-de-utilizacao',
    title: 'Termos de utilização',
    blocks: [
      {
        paragraphs: [
          'Salvo disposição em contrário, os termos de utilização previstos nesta secção aplicam-se de modo geral à utilização desta Aplicação. Podem aplicar-se condições adicionais em situações específicas, sendo nesse caso indicadas no presente documento.',
          'Ao utilizar esta Aplicação, o Utilizador confirma que não se qualifica como Consumidor e que atua no âmbito da sua atividade profissional ou empresarial.',
        ],
      },
      {
        heading: 'Registo de conta',
        paragraphs: [
          'Para utilizar o Serviço, o Utilizador deve registar-se ou criar uma conta, facultando de forma completa e verdadeira todos os dados necessários. Caso contrário, o Serviço ficará indisponível.',
          'O Utilizador é responsável por manter as suas credenciais de início de sessão confidenciais e seguras, devendo escolher chaves de acesso que satisfaçam os mais elevados padrões de segurança permitidos por esta Aplicação.',
          'Ao registar-se, o Utilizador assume toda a responsabilidade por qualquer atividade que ocorra sob as suas credenciais. O Utilizador deve informar imediatamente o Proprietário, através dos contactos indicados no presente documento, se considerar que as suas contas, credenciais ou dados pessoais foram violados, indevidamente divulgados ou roubados.',
          'O Utilizador é responsável pelas contas que crie para os seus colaboradores, bem como pelos acessos que lhes atribua, devendo revogá-los quando deixem de ser necessários.',
        ],
      },
      {
        heading: 'Encerramento da conta',
        paragraphs: [
          'O Utilizador pode encerrar a sua conta e deixar de utilizar o Serviço a qualquer momento, contactando diretamente o Proprietário através dos contactos indicados no presente documento.',
          'O encerramento produz efeitos no termo do período de subscrição já pago, não conferindo direito a reembolso do período em curso, salvo acordo escrito em contrário.',
          'Antes do encerramento, o Utilizador pode solicitar a exportação dos dados que introduziu na Aplicação, nos termos previstos no Acordo de Tratamento de Dados.',
        ],
      },
      {
        heading: 'Eliminação e suspensão da conta',
        paragraphs: [
          'O Proprietário reserva-se o direito de suspender ou eliminar contas de Utilizadores que violem os presentes Termos, a lei aplicável ou os direitos de terceiros, ou que representem um risco para o Serviço, para outros Utilizadores ou para terceiros.',
          'Sempre que a natureza da violação o permita, o Proprietário notificará previamente o Utilizador e concederá um prazo razoável para sanar a situação. Em caso de risco grave e iminente para a segurança do Serviço ou de terceiros, a suspensão pode ser imediata, sendo o Utilizador notificado logo que possível.',
          'A suspensão ou eliminação de contas por causas imputáveis ao Utilizador não confere direito a indemnização, compensação ou reembolso, nem isenta o Utilizador do pagamento dos valores devidos.',
        ],
      },
      {
        heading: 'Conteúdo nesta Aplicação',
        paragraphs: [
          'Salvo indicação em contrário, ou quando seja claramente reconhecível, qualquer conteúdo disponível nesta Aplicação é detido ou fornecido pelo Proprietário ou pelos seus licenciantes, que reservam todos os direitos de propriedade intelectual sobre esse conteúdo.',
          'O Utilizador não pode copiar, descarregar, partilhar, modificar, traduzir, transformar, publicar, transmitir, vender, sublicenciar, editar, ceder a terceiros ou criar trabalhos derivados a partir desse conteúdo, para além do que seja necessário ou implícito na utilização adequada do Serviço, nem permitir que terceiros o façam.',
          'Nenhuma limitação ou exceção legal aplicável aos direitos de autor é afetada pelo disposto nesta secção.',
        ],
      },
      {
        heading: 'Conteúdo fornecido pelo Utilizador',
        paragraphs: [
          'O Proprietário autoriza o Utilizador a carregar, partilhar ou fornecer o seu próprio conteúdo nesta Aplicação, incluindo dados de clientes, serviços, colaboradores e marcações.',
          'Ao fornecer conteúdo, o Utilizador confirma que está legalmente autorizado a fazê-lo e que não viola disposições legais ou direitos de terceiros. O Utilizador é exclusivamente responsável pelo conteúdo que fornece.',
          'O Utilizador concede ao Proprietário uma licença não exclusiva, isenta de royalties e limitada ao estritamente necessário para operar, manter, proteger e disponibilizar o Serviço. Esta licença não confere ao Proprietário o direito de utilizar o conteúdo do Utilizador para quaisquer outras finalidades, designadamente comerciais ou promocionais, e cessa com a eliminação do conteúdo ou o termo do Contrato.',
          'O Proprietário reserva-se o direito de recusar, remover ou bloquear conteúdo que viole os presentes Termos, a lei aplicável ou direitos de terceiros, ou que represente um risco para o Serviço, notificando o Utilizador sempre que possível.',
        ],
      },
      {
        heading: 'Acesso a recursos externos',
        paragraphs: [
          'Através desta Aplicação, o Utilizador pode aceder a recursos externos fornecidos por terceiros. O Proprietário não tem controlo sobre tais recursos, não sendo responsável pelo seu conteúdo nem pela sua disponibilidade.',
          'As condições aplicáveis a esses recursos resultam dos termos e condições de cada terceiro ou, na sua ausência, da lei aplicável.',
        ],
      },
      {
        heading: 'Utilização aceitável',
        paragraphs: [
          'Esta Aplicação e o Serviço apenas podem ser utilizados no âmbito para que se destinam, nos termos dos presentes Termos e da lei aplicável. O Utilizador é exclusivamente responsável por garantir que a sua utilização não viola qualquer lei, regulamento ou direito de terceiros.',
          'O Proprietário reserva-se o direito de adotar as medidas necessárias para proteger os seus interesses legítimos, incluindo negar o acesso ao Serviço, resolver o Contrato ou comunicar às autoridades competentes qualquer conduta inadequada, sempre que o Utilizador se envolva em alguma das seguintes atividades:',
        ],
        bullets: [
          'violação de leis, regulamentos ou dos presentes Termos;',
          'violação de direitos de terceiros, incluindo direitos dos seus próprios clientes;',
          'utilização do Serviço para envio de comunicações não solicitadas em violação das regras aplicáveis ao marketing direto;',
          'prejuízo considerável dos interesses legítimos do Proprietário;',
          'tentativa de aceder, testar ou comprometer a segurança do Serviço sem autorização escrita prévia.',
        ],
      },
      {
        heading: 'Licença de software',
        paragraphs: [
          'Quaisquer direitos de propriedade intelectual ou industrial sobre o software e as aplicações técnicas incorporadas ou relacionadas com esta Aplicação são detidos pelo Proprietário ou pelos seus licenciantes.',
          'Sujeito ao cumprimento dos presentes Termos, o Proprietário concede ao Utilizador uma licença revogável, não exclusiva, não sublicenciável e intransmissível para utilizar o software no âmbito e para efeitos do Serviço contratado.',
          'Esta licença não confere ao Utilizador quaisquer direitos de acesso, utilização ou divulgação do código-fonte. Todos os direitos e licenças concedidos cessam imediatamente com a cessação ou caducidade do Contrato.',
        ],
      },
    ],
  },
  {
    id: 'subscricao-e-pagamento',
    title: 'Subscrição, preços e pagamento',
    blocks: [
      {
        paragraphs: [
          'O Serviço é disponibilizado mediante subscrição mensal. Os preços aplicáveis são os indicados na proposta comercial aceite pelo Utilizador ou, na sua falta, os publicados nesta Aplicação à data da subscrição.',
          'A subscrição renova-se automaticamente por períodos mensais sucessivos, salvo denúncia por qualquer das partes antes do termo do período em curso. Não existe período de fidelização mínimo.',
        ],
      },
      {
        heading: 'Mensagens SMS',
        paragraphs: [
          'Cada plano inclui um número determinado de mensagens SMS por mês. As mensagens não utilizadas não transitam para o mês seguinte.',
          'O consumo que exceda o volume incluído no plano é faturado através de pacotes adicionais, ao preço em vigor à data do consumo. O Utilizador é responsável pelo conteúdo das mensagens enviadas através do Serviço e pelo cumprimento das regras aplicáveis ao envio de comunicações comerciais.',
        ],
      },
      {
        heading: 'Alterações de preço',
        paragraphs: [
          'O Proprietário pode alterar os preços do Serviço, notificando o Utilizador com uma antecedência mínima de 30 dias. A alteração produz efeitos no início do período de subscrição seguinte. Se o Utilizador não aceitar o novo preço, pode denunciar a subscrição antes da sua entrada em vigor, sem qualquer penalização.',
        ],
      },
      {
        heading: 'Atraso no pagamento',
        paragraphs: [
          'Em caso de falta de pagamento, o Proprietário notificará o Utilizador e concederá um prazo razoável para regularização. Decorrido esse prazo sem pagamento, o Proprietário pode suspender o acesso ao Serviço até à regularização, mantendo os dados do Utilizador disponíveis durante o período de conservação aplicável.',
        ],
      },
      {
        heading: 'Garantia de resultado',
        paragraphs: [
          'Quando o Proprietário anuncie uma garantia de resultado associada à ativação dos lembretes, aplicam-se as condições divulgadas nesta Aplicação à data da subscrição. O Utilizador deve invocar a garantia por escrito até 30 dias após o termo do primeiro mês de subscrição, permitindo ao Proprietário verificar os dados de utilização necessários à sua apreciação.',
        ],
      },
    ],
  },
  {
    id: 'protecao-de-dados',
    title: 'Proteção de dados',
    blocks: [
      {
        paragraphs: [
          'O tratamento de dados pessoais relativos ao Utilizador enquanto cliente do Proprietário é descrito na Política de Privacidade desta Aplicação, que se considera incorporada nos presentes Termos.',
          'Quando o Utilizador trata dados pessoais dos seus próprios clientes, colaboradores ou contactos através da Aplicação, o Utilizador atua como Responsável pelo Tratamento e o Proprietário como Subcontratante. Essa relação é regulada pelo Acordo de Tratamento de Dados, que faz parte integrante dos presentes Termos e prevalece sobre estes em matéria de proteção de dados.',
          'O Utilizador é responsável por assegurar a existência de fundamento de licitude para o tratamento dos dados que introduz na Aplicação e por prestar aos titulares dos dados as informações legalmente exigidas, incluindo quanto ao envio de lembretes, confirmações e comunicações de marketing.',
        ],
      },
    ],
  },
  {
    id: 'responsabilidade',
    title: 'Responsabilidade e indemnização',
    blocks: [
      {
        paragraphs: [
          'Salvo disposição expressa em contrário, a responsabilidade do Proprietário por danos relacionados com o presente Contrato é excluída, limitada ou reduzida na máxima medida permitida pela lei aplicável.',
        ],
      },
      {
        heading: 'Indemnização',
        paragraphs: [
          'O Utilizador aceita indemnizar e isentar o Proprietário, bem como os seus colaboradores, agentes e parceiros, de quaisquer reclamações, danos, responsabilidades, custos e despesas, incluindo honorários de advogado, apresentados por terceiros em consequência da utilização culposa do Serviço pelo Utilizador, do incumprimento dos presentes Termos, da violação de direitos de terceiros ou da violação de disposições legais, na medida permitida pela lei aplicável.',
          'O disposto acima aplica-se igualmente a reclamações apresentadas contra o Proprietário pelos clientes ou colaboradores do Utilizador relativamente a dados ou comunicações geridos pelo Utilizador através do Serviço.',
        ],
      },
      {
        heading: 'Limitação de responsabilidade',
        paragraphs: [
          'A limitação de responsabilidade prevista nesta secção não se aplica a danos causados à vida, à saúde ou à integridade física, a danos resultantes do incumprimento de uma obrigação contratual essencial, nem a danos resultantes de dolo ou negligência grave.',
          'Fora dessas situações, e desde que a Aplicação tenha sido utilizada de forma correta e adequada, o Proprietário responde apenas pelos danos típicos e previsíveis no momento da celebração do Contrato.',
          'Dentro destes limites, o Proprietário não é responsável, designadamente, por:',
        ],
        bullets: [
          'danos resultantes de interrupções ou defeitos de funcionamento devidos a força maior ou a eventos imprevisíveis e fora do seu controlo, tais como falhas de linhas elétricas ou de telecomunicações, indisponibilidade da Internet, greves, desastres naturais, vírus e ciberataques, ou interrupções em serviços de terceiros;',
          'perdas de oportunidades de negócio e outros danos indiretos, incluindo perdas de exploração, de receitas, de lucros ou de poupanças previstas, perda de contratos ou de relações comerciais, e perda de reputação;',
          'danos que não resultem diretamente do incumprimento dos presentes Termos pelo Proprietário;',
          'danos resultantes de vírus ou outro software malicioso contido em ficheiros descarregados através da Internet, cabendo ao Utilizador implementar medidas de segurança adequadas.',
        ],
      },
      {
        heading: 'Limite máximo',
        paragraphs: [
          'Em qualquer situação de responsabilidade, e na medida permitida pela lei aplicável, a indemnização devida pelo Proprietário não excederá o total dos pagamentos recebidos do Utilizador ao abrigo do Contrato nos 12 meses anteriores ao facto gerador da responsabilidade, ou durante a vigência do Contrato se esta for inferior.',
        ],
      },
    ],
  },
  {
    id: 'disposicoes-comuns',
    title: 'Disposições comuns',
    blocks: [
      {
        heading: 'Inexistência de renúncia',
        paragraphs: [
          'O não exercício pelo Proprietário de qualquer direito ou disposição dos presentes Termos não constitui renúncia a esse direito ou disposição, nem a qualquer outro.',
        ],
      },
      {
        heading: 'Interrupção do Serviço',
        paragraphs: [
          'Para assegurar o melhor nível de serviço possível, o Proprietário reserva-se o direito de interromper o Serviço para manutenção, atualizações do sistema ou outras alterações, informando o Utilizador com antecedência razoável sempre que a interrupção seja planeada.',
          'O Proprietário pode ainda decidir suspender ou descontinuar o Serviço, notificando o Utilizador com uma antecedência mínima de 30 dias. Nesse caso, colaborará com o Utilizador na exportação dos seus dados antes da cessação e reembolsará a parte da subscrição já paga e não utilizada.',
          'O Serviço pode ainda ficar indisponível por razões fora do controlo razoável do Proprietário, tais como falhas de infraestrutura ou de fornecedores.',
        ],
      },
      {
        heading: 'Revenda do Serviço',
        paragraphs: [
          'O Utilizador não pode reproduzir, duplicar, copiar, vender, revender ou explorar qualquer parte desta Aplicação ou do Serviço sem autorização prévia e escrita do Proprietário.',
        ],
      },
      {
        heading: 'Direitos de propriedade intelectual',
        paragraphs: [
          'Quaisquer direitos de propriedade intelectual relacionados com esta Aplicação, incluindo direitos de autor, marcas, patentes e desenhos, são propriedade exclusiva do Proprietário ou dos seus licenciantes, estando sujeitos à proteção conferida pela lei e pelos tratados internacionais aplicáveis.',
          'Todas as marcas, nomes comerciais, ilustrações, imagens e logótipos associados a esta Aplicação permanecem propriedade exclusiva do Proprietário ou dos seus licenciantes.',
        ],
      },
      {
        heading: 'Alterações aos presentes Termos',
        paragraphs: [
          'O Proprietário reserva-se o direito de alterar os presentes Termos, informando o Utilizador com uma antecedência mínima de 30 dias relativamente à data em que as alterações produzem efeitos.',
          'As alterações apenas afetam a relação com o Utilizador a partir da data comunicada. A utilização continuada do Serviço após essa data significa a aceitação dos Termos revistos. Se o Utilizador não pretender ficar vinculado às alterações, pode denunciar o Contrato antes da sua entrada em vigor, sem qualquer penalização.',
          'A versão anterior rege a relação até à aceitação da nova versão. O Utilizador pode obter junto do Proprietário qualquer versão anterior dos presentes Termos.',
        ],
      },
      {
        heading: 'Cessão do contrato',
        paragraphs: [
          'O Proprietário pode transferir, ceder ou subcontratar quaisquer direitos ou obrigações previstos nos presentes Termos, tendo em consideração os interesses legítimos do Utilizador. O Utilizador não pode ceder ou transferir os seus direitos ou obrigações sem autorização escrita do Proprietário.',
        ],
      },
      {
        heading: 'Notificações',
        paragraphs: [
          `Todas as comunicações relativas à utilização desta Aplicação devem ser enviadas para ${PRIVACY_POLICY_OWNER_EMAIL} ou para a morada indicada no início do presente documento.`,
        ],
      },
      {
        heading: 'Divisibilidade das cláusulas',
        paragraphs: [
          'Se alguma disposição dos presentes Termos for ou se tornar inválida ou inexequível nos termos da lei aplicável, tal não afeta a validade das restantes disposições, que se mantêm plenamente em vigor.',
          'Nesse caso, as partes envidarão os seus melhores esforços para acordar disposições válidas e exequíveis que substituam as disposições inválidas, tendo em conta a sua finalidade original. Não sendo possível o acordo, aplicam-se as disposições legais supletivas.',
        ],
      },
      {
        heading: 'Legislação aplicável e foro competente',
        paragraphs: [
          'Os presentes Termos são regidos pela lei portuguesa, independentemente de quaisquer princípios relativos a conflitos de leis.',
          'Para a resolução de qualquer litígio emergente dos presentes Termos, ou com eles relacionado, é competente o Tribunal Judicial da Comarca dos Açores, com expressa renúncia a qualquer outro.',
        ],
      },
    ],
  },
  {
    id: 'acessibilidade',
    title: 'Acessibilidade',
    blocks: [
      {
        paragraphs: [
          'O Proprietário compromete-se a tornar o conteúdo acessível a Utilizadores com deficiência. Caso um Utilizador não consiga aceder a qualquer parte desta Aplicação em razão de uma deficiência, deve comunicar o problema, incluindo uma descrição detalhada, através dos contactos indicados no presente documento. Sempre que o problema possa ser identificado e resolvido de acordo com as técnicas e ferramentas padrão da indústria, o Proprietário compromete-se a resolvê-lo com a maior brevidade possível.',
        ],
      },
    ],
  },
  {
    id: 'definicoes',
    title: 'Definições e referências jurídicas',
    blocks: [
      {
        heading: 'Esta Aplicação',
        paragraphs: [
          'O site e a aplicação BelaHora, enquanto meio que permite a prestação do Serviço.',
        ],
      },
      {
        heading: 'Contrato',
        paragraphs: [
          'Qualquer relação juridicamente vinculativa ou contratual entre o Proprietário e o Utilizador que seja regida pelos presentes Termos.',
        ],
      },
      {
        heading: 'Consumidor',
        paragraphs: [
          'A pessoa singular que atue com fins alheios à sua atividade comercial, industrial, artesanal ou profissional, nos termos da lei aplicável.',
        ],
      },
      {
        heading: 'Utilizador Comercial',
        paragraphs: [
          'Qualquer Utilizador que não se qualifique como Consumidor, ou seja, que utilize esta Aplicação no âmbito da sua atividade comercial, industrial, artesanal ou profissional.',
        ],
      },
      {
        heading: 'Proprietário (ou Nós)',
        paragraphs: [
          `${PRIVACY_POLICY_OWNER_NAME}, enquanto pessoa que presta esta Aplicação e o Serviço ao Utilizador.`,
        ],
      },
      {
        heading: 'Serviço',
        paragraphs: [
          'O serviço prestado através desta Aplicação, tal como descrito nos presentes Termos e nesta Aplicação.',
        ],
      },
      {
        heading: 'Termos',
        paragraphs: [
          'Todas as disposições aplicáveis à utilização desta Aplicação e do Serviço tal como descritas no presente documento, incluindo qualquer outro documento ou acordo relacionado, periodicamente atualizados.',
        ],
      },
      {
        heading: 'Utilizador',
        paragraphs: [
          'Qualquer pessoa singular ou coletiva que utilize esta Aplicação.',
        ],
      },
      {
        heading: 'Acordo de Tratamento de Dados',
        paragraphs: [
          'O acordo celebrado entre o Utilizador, na qualidade de Responsável pelo Tratamento, e o Proprietário, na qualidade de Subcontratante, disponível em /dpa.',
        ],
      },
      {
        paragraphs: [
          'Os presentes Termos respeitam apenas a esta Aplicação, salvo indicação em contrário no presente documento.',
        ],
      },
    ],
  },
];
